import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook } from 'lucide-react';
import { contactData, navLinks } from '../data/mock';

const Footer = () => {
  const whatsappNumber = '917073620688';

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const iconMap = {
    instagram: Instagram,
    linkedin: Linkedin,
    facebook: Facebook
  };

  const socialColorMap = {
    instagram: 'hover:bg-gradient-to-r hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]',
    linkedin: 'hover:bg-[#0A66C2]',
    facebook: 'hover:bg-[#1877F2]'
  };

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3
              className="text-2xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Drishti Nimawat <span className="text-[#e8d5c4]">Designs</span>
            </h3>
            <p
              className="text-white/60 mb-6"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Crafting timeless spaces with elegance and sophistication.
            </p>
            <div className="flex space-x-4">
              {contactData.socials.map((social) => {
                const Icon = iconMap[social.icon] || Instagram;
                const colorClass = socialColorMap[social.icon];
                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    whileHover={{ scale: 1.1, y: -3 }}
                    className={`p-2 bg-white/10 text-white transition-all duration-300 ${colorClass}`}
                    aria-label={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon size={20} />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-lg font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-white/60 hover:text-[#e8d5c4] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4
              className="text-lg font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Services
            </h4>
            <ul className="space-y-2 text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Interior Design</li>
              <li>Architectural Design</li>
              <li>3D Visualization</li>
              <li>Design Consultation</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4
              className="text-lg font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Get In Touch
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail size={20} className="text-[#e8d5c4] flex-shrink-0 mt-1" />
                <a
                  href={`mailto:${contactData.email}`}
                  className="text-white/60 hover:text-[#e8d5c4] transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {contactData.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={20} className="text-[#e8d5c4] flex-shrink-0 mt-1" />
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  className="text-white/60 hover:text-[#e8d5c4] transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contactData.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={20} className="text-[#e8d5c4] flex-shrink-0 mt-1" />
                <span
                  className="text-white/60"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {contactData.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-center">
            <p
              className="text-white/40 text-sm text-center md:text-left"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              © {new Date().getFullYear()} Drishti Nimawat Designs. All rights reserved.
            </p>
            <p
              className="text-white/40 text-sm text-center"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Designed and built by Deeptanshu Sharma
            </p>
            <div className="flex justify-center md:justify-end space-x-6">
              <a
                href="#"
                className="text-white/40 hover:text-[#e8d5c4] text-sm transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-white/40 hover:text-[#e8d5c4] text-sm transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
